import {
  formatSizeBreakdown,
  type QuoteDestination,
  SHIPPING_REGION_LABELS,
} from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { formatCount } from '@/shared/lib';
import { cn, tableStyles } from '@/shared/ui';

export interface QuoteDestinationsTableProps {
  readonly destinations: readonly QuoteDestination[];
}

// 라벨-값 목록 안의 작은 표라 공용 표보다 칸을 조금 좁힌다.
const TH_CLASS = cn(tableStyles.th, 'px-4');
const TD_CLASS = cn(tableStyles.td, 'px-4 py-3 align-top');
const PENDING = '주문 때 입력';

/**
 * 견적 요청 배송지 표(SUPER-86 §6-2) — 받는 분·연락처·주소·수량·지역, 배송지 순서대로. 비회원 출처는 받는 분·연락처·상세주소가
 * 없다(주문할 때 받는다, D8). 지역을 알 수 없는 배송지(region null)는 '-'. 배송지가 없는 견적은 표 대신 안내.
 */
export const QuoteDestinationsTable = ({
  destinations,
}: QuoteDestinationsTableProps): ReactElement => {
  if (destinations.length === 0) {
    return <span className="text-muted-foreground">배송지 없음 — 주문할 때 받습니다.</span>;
  }
  const ordered = [...destinations].sort((a, b) => a.sortOrder - b.sortOrder);
  return (
    <div className="overflow-x-auto rounded-xl border border-foreground/[0.06]">
      <table aria-label="배송지" className={tableStyles.table}>
        <thead className={tableStyles.head}>
          <tr>
            <th className={TH_CLASS}>#</th>
            <th className={TH_CLASS}>받는 분</th>
            <th className={TH_CLASS}>연락처</th>
            <th className={TH_CLASS}>주소</th>
            <th className={cn(TH_CLASS, 'text-right')}>수량</th>
            <th className={TH_CLASS}>지역</th>
          </tr>
        </thead>
        <tbody>
          {ordered.map((destination, index) => (
            <tr key={destination.id} className={tableStyles.row}>
              <td className={TD_CLASS}>{index + 1}</td>
              <td className={TD_CLASS}>{destination.recipientName ?? PENDING}</td>
              <td className={TD_CLASS}>{destination.recipientPhone ?? PENDING}</td>
              <td className={TD_CLASS}>
                {[`(${destination.postalCode})`, destination.address1, destination.address2]
                  .filter((part) => part !== null && part !== '')
                  .join(' ')}
              </td>
              <td className={cn(TD_CLASS, 'text-right tabular-nums')}>
                {formatCount(destination.quantity)}개
                {destination.sizes.length > 0 ? (
                  <span className="block text-xs text-muted-foreground">
                    {formatSizeBreakdown(destination.sizes)}
                  </span>
                ) : null}
              </td>
              <td className={TD_CLASS}>
                {destination.region === null ? '-' : SHIPPING_REGION_LABELS[destination.region]}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
